import React, {useState, useRef, useEffect} from 'react';
import classNames from 'classnames';

type AccordionProps = {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
};

const ACCORDION_CONTAINER_DATA_ID = 'accordion-content-container';

export const Accordion = ({
  title,
  children,
  defaultOpen = false,
}: AccordionProps) => {
  const [isOpened, setOpened] = useState<boolean>(defaultOpen);
  const contentElement = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<string>(defaultOpen ? 'auto' : '0px');
  // Need to start height at 'auto' so that the defaultOpen items are included for SEO. Setting the height programatically after the first render would exclude it from SEO

  useEffect(() => {
    // This effect sets the height of the opened items after their ref has been set. Without this, height will be at 'auto', and when it sets to '0px', it skips the animation.
    if (isOpened) {
      const contentHeight = contentElement.current?.scrollHeight;
      setHeight(`${contentHeight}px`);
    } else {
      setHeight('0px');
    }
  }, [contentElement, isOpened]);

  const handleOpening = () => {
    setOpened((previousIsOpened) => !previousIsOpened);
    setHeight(!isOpened ? `${contentElement.current?.scrollHeight}px` : '0px');
  };

  return (
    <div
      data-id={ACCORDION_CONTAINER_DATA_ID}
      className={classNames(
        'flex',
        'flex-col',
        'rounded',
        'border-b-3',
        'border-black',
        'p-6',
        'md:py-8',
        'md:pl-12',
        'lg:ml-32',
        'lg:mr-16',
      )}
    >
      <button
        onClick={handleOpening}
        className={classNames(
          'flex',
          'flex-row',
          'text-start',
          'justify-between',
        )}
      >
        <h2
          className={classNames(
            'font-bold',
            'leading-[31px]',
            'text-[26px]',
            'mr-5',
            'md:leading-9',
            'md:text-3xl',
          )}
        >
          {title}
        </h2>
        <span
          className={classNames(
            'flex',
            'h-11',
            'w-11',
            'shrink-0',
            'items-center',
            'justify-center',
            'self-baseline',
            'rounded-full',
            'fill-white',
            'bg-black',
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={classNames('w-2', 'md:w-3')}
            width="20"
            height="20"
            viewBox="0 0 160 160"
          >
            <rect
              className={classNames(
                'origin-[50%_50%]',
                'duration-500',
                isOpened ? 'rotate-90' : 'scale-100',
              )}
              x="70"
              width="20"
              height="160"
            />
            <rect className="horizontal-line" y="70" width="160" height="20" />
          </svg>
        </span>
      </button>
      <div
        ref={contentElement}
        style={{height}}
        className={classNames(
          'bg',
          'overflow-hidden',
          'transition-all',
          'duration-500',
        )}
      >
        <p
          className={classNames(
            'font-medium',
            'leading-[30px]',
            'mt-4',
            'text-xl',
            {
              hidden: !isOpened && height === '0px',
            },
          )}
        >
          {children}
        </p>
      </div>
    </div>
  );
};
